<div class="landing-page h-full flex items-center justify-center p-8 bg-base-200 overflow-y-auto" data-testid="landing-page-section">
  <div class="max-w-4xl w-full my-auto">
    <div class="bg-base-100 rounded-lg shadow-xl p-8 relative">
      <!-- Close Button -->
      <button class="btn btn-sm btn-circle btn-ghost absolute right-4 top-4"
        (click)="onCloseModal.emit()"
        title="Close and return later"
        aria-label="Close and return later"
        data-testid="landing-page-close-btn">
        <app-icon name="close" size="sm"></app-icon>
      </button>

      <!-- Course Thumbnail -->
      @if (courseData.thumbnailUrl || courseData.imageUrl) {
        <div class="text-center mb-6">
          <img [src]="courseData.thumbnailUrl || courseData.imageUrl"
            [alt]="courseData.title || courseData.name"
            class="mx-auto rounded-lg shadow-md max-w-md w-full h-auto object-cover">
        </div>
      }

      <!-- Course Title -->
      <h1 class="text-4xl font-bold text-center mb-4 user-text-wrap"
          [title]="courseData.title || courseData.name"
          data-testid="landing-page-title-heading">{{ courseData.title || courseData.name }}</h1>

      <!-- Course Description -->
      @if (courseData.description) {
        <div class="description-container mb-8"
          [class.has-overflow]="isDescriptionLong(courseData.description)"
          [class.is-expanded]="isDescriptionExpanded">
          <p class="text-lg text-center text-base-content/80 description-content user-text-wrap"
            [class.truncated]="isDescriptionLong(courseData.description) && !isDescriptionExpanded"
            [class.expanded]="isDescriptionExpanded">
            {{ courseData.description }}
          </p>
          @if (isDescriptionLong(courseData.description)) {
            <button
              class="show-more-btn mx-auto"
              (click)="toggleDescriptionExpand()"
              [attr.aria-expanded]="isDescriptionExpanded">
              {{ isDescriptionExpanded ? 'Show less' : 'Show more' }}
              <app-icon [name]="isDescriptionExpanded ? 'expand_less' : 'expand_more'" size="sm"></app-icon>
            </button>
          }
        </div>
      }

      <!-- Course Info Stats -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div class="bg-base-200 rounded-lg p-6 text-center">
          <div class="text-sm text-base-content/70 mb-2">Total Units</div>
          <div class="text-4xl font-bold text-primary">{{ totalUnitsCount }}</div>
        </div>
        <div class="bg-base-200 rounded-lg p-6 text-center">
          <div class="text-sm text-base-content/70 mb-2">Total Modules</div>
          <div class="text-4xl font-bold text-secondary">{{ totalModulesCount }}</div>
        </div>
        <div class="bg-base-200 rounded-lg p-6 text-center">
          <div class="text-sm text-base-content/70 mb-2">Certificate</div>
          <div class="text-4xl font-bold text-accent">
            <!-- KAN-411 round 4b: GET /courses/:id camelizes the flat
                 certificate_enabled column to certificateEnabled; the nested
                 certificate.enabled shape isn't on the read response, so the
                 indicator showed the red X even for a certificate-enabled course.
                 Prefer the flat field. -->
            <app-icon [name]="(courseData.certificateEnabled ?? courseData.certificate?.enabled) ? 'check' : 'close'" size="lg" data-testid="landing-certificate-icon"></app-icon>
          </div>
        </div>
      </div>

      <!-- Organization Badge (if available) -->
      @if (courseData.organization_name) {
        <div class="text-center mb-8">
          <div class="badge badge-lg badge-primary">
            {{ courseData.organization_name }}
          </div>
        </div>
      }

      <!-- Action Buttons - Different for new vs returning learners -->
      <div class="text-center">
        <!-- New Learner: Start Course -->
        @if (!isReturningLearner) {
          <button
            (click)="onStart.emit()"
            class="btn btn-primary btn-lg shadow-lg"
            data-testid="landing-page-start-btn">
            <app-icon name="play_arrow" size="md"></app-icon>
            Start Course
          </button>
        }

        <!-- Returning Learner: Resume Course + Skip option + Reset -->
        @if (isReturningLearner) {
          <div class="flex flex-col items-center gap-3">
            <button (click)="onResume.emit()" class="btn btn-primary btn-lg shadow-lg" data-testid="landing-page-resume-btn">
              <app-icon [name]="isCompleted ? 'replay' : 'play_arrow'" size="md"></app-icon>
              {{ isCompleted ? 'Replay Course' : 'Resume Course' }}
            </button>
            <button (click)="onSkip.emit()" class="btn btn-ghost btn-sm" data-testid="landing-page-skip-btn">
              Skip to content
              <app-icon name="arrow_forward" size="sm"></app-icon>
            </button>
            <!-- Self-Reset Button -->
            @if (canSelfReset && !showResetConfirmation && !isResetting) {
              <div class="mt-4 flex flex-col items-center gap-1">
                <button (click)="confirmReset()"
                  class="btn btn-outline btn-error btn-sm"
                  data-testid="landing-page-reset-btn">
                  <app-icon name="restart_alt" size="sm"></app-icon>
                  Reset My Progress
                </button>
                @if (maxResets !== null) {
                  <span class="text-xs text-base-content/50">
                    {{ resetsUsed }} of {{ maxResets }} resets used
                  </span>
                }
              </div>
            }
            <!-- Reset Confirmation -->
            @if (showResetConfirmation) {
              <div class="mt-4 alert alert-warning max-w-md" data-testid="reset-confirmation-alert">
                <div class="flex flex-col gap-2 w-full">
                  <div class="flex items-start gap-2">
                    <app-icon name="warning" size="sm" class="flex-shrink-0 mt-0.5"></app-icon>
                    <span class="text-sm">
                      This will erase all your progress, quiz submissions, and certificates for this course.
                      This action cannot be undone.
                    </span>
                  </div>
                  <div class="flex gap-2 justify-end">
                    <button (click)="cancelReset()" class="btn btn-ghost btn-xs" data-testid="reset-cancel-btn">
                      Cancel
                    </button>
                    <button (click)="executeReset()" class="btn btn-error btn-xs" data-testid="reset-confirm-btn">
                      <app-icon name="restart_alt" size="sm"></app-icon>
                      Confirm Reset
                    </button>
                  </div>
                </div>
              </div>
            }
            <!-- Resetting Spinner -->
            @if (isResetting) {
              <div class="mt-4 flex items-center gap-2 text-base-content/70">
                <span class="loading loading-spinner loading-sm"></span>
                <span class="text-sm">Resetting progress...</span>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>
</div>
